// Dashboard (handoff §7; V4 Dashboard) — the shapes GET /api/dashboard*
// returns, and the few pure rules the page applies on top: the date presets,
// the calendar dots, which cards share a row, and the weather readings.
// Figures themselves are the server's (services/dashboard-build.js).

import { addDaysLocal, nowHmBusiness, parseIsoLocal } from '../timesheets/time'
import type { UnauthorisedTagUse } from '../../components/UnauthorisedTagsAlert'
import type { WaitingTagReceipts } from '../../components/WaitingReceiptsAlert'
import type { Level, Preset } from '../productActivity/DatePeriod'

export interface Kpi {
  key: 'kg_items' | 'cost_all' | 'cost_pickers' | 'kg_hour'
  label: string
  value: number | null
  previous: number | null
  change_pct: number | null
  compare_label: string
  rise: 'good' | 'neutral'
  items?: number
  previous_items?: number
}

// The four pay fields are left out for anyone without pay_data (approved D1
// data boundary, item 7), and the rows are then ranked on KG/Hr.
export interface LeaderRow {
  rank: number
  human_id: string
  name: string
  hours: number
  hourly?: number
  // Receipters board only: how many people they received for.
  pickers?: number
  items: number
  kg: number
  kg_hour: number
  personal_minimum?: number | null
  minimum_partial?: boolean
  below_minimum?: boolean
  // Still clocked in: hours run to the last scan, less the scheduled breaks so far.
  in_progress?: boolean
  // Still on shift with nothing picked or earned yet: Hrly shows a dash.
  no_picks_yet?: boolean
}

export interface WeekBar {
  week: number
  this_season: { start: string; kg: number | null }
  last_season: { start: string | null; kg: number | null }
}

export interface DashResponse {
  window: { from: string; to: string; days: number }
  previous: { from: string; to: string }
  season_start: string | null
  teams: { id: string; name: string }[]
  kpis: Kpi[]
  kpi_notes: string[]
  // Set when the two labour-cost cards and leaderboard earnings were left out.
  pay_hidden?: boolean
  // teams: the board's team choices, the teams someone on it worked in over the period.
  leaderboard: { team: string | null; rows: LeaderRow[]; notes: string[]; teams?: { id: string; name: string }[] }
  // Who received items credited to someone else: their own hours (and Hrly),
  // with the items and weight they received.
  // pickers: unique pickers across the board, each counted once however many receipters scanned for them.
  receipters: { rows: LeaderRow[]; pickers?: number; teams?: { id: string; name: string }[] }
  // The boards turned on for everyone in the workspace, and whether this person may change that.
  workspace: { widgets_on: WidgetId[]; can_edit: boolean }
  weekly: { season: number; this_label: string; last_label: string; weeks: WeekBar[] }
  // Tags another workspace owns, scanned here; null when none.
  unauthorised_tag_use?: UnauthorisedTagUse | null
  // Receipts waiting on a picker Control doesn't know yet; null when none.
  waiting_tag_receipts?: WaitingTagReceipts | null
}

export interface RosterWorker {
  shift_id: string
  human_id: string
  name: string
  team_name: string
  // The open session's start, and the day's first clock-on (earlier when they have moved team).
  start_time: string
  clock_on: string
  groups: string[]
  job_roles: string[]
  items: number
  last_scan: { time: string; location: string | null } | null
}

export interface RosterResponse {
  today: string
  workers: RosterWorker[]
  on_timesheet: number
  earlier_open: number
}

export interface ClockOffPreview {
  preview: boolean
  mode: 'time' | 'last_scan'
  today: string
  count: number
  rows: { shift_id: string; human_id: string; worker_name: string; work_date: string; finish_time: string }[]
  blocked: { shift_id: string; human_id: string; worker_name: string; why: string }[]
  earlier_open: number
}

// A reply missing a part draws that card empty rather than taking the page
// down with it.
const arr = <T,>(v: unknown): T[] => (Array.isArray(v) ? v : [])
export function normaliseDash(r: any): DashResponse {
  return {
    ...r,
    teams: arr(r?.teams),
    kpis: arr(r?.kpis),
    kpi_notes: arr(r?.kpi_notes),
    leaderboard: { team: r?.leaderboard?.team ?? null, rows: arr(r?.leaderboard?.rows), notes: arr(r?.leaderboard?.notes), teams: r?.leaderboard?.teams && arr(r.leaderboard.teams) },
    receipters: { rows: arr(r?.receipters?.rows), pickers: Number(r?.receipters?.pickers) || 0, teams: r?.receipters?.teams && arr(r.receipters.teams) },
    workspace: { widgets_on: arr(r?.workspace?.widgets_on), can_edit: r?.workspace?.can_edit === true },
    weekly: { season: r?.weekly?.season, this_label: r?.weekly?.this_label ?? '', last_label: r?.weekly?.last_label ?? '', weeks: arr(r?.weekly?.weeks) },
  }
}
export function normaliseRoster(r: any): RosterResponse {
  const workers = arr(r?.workers).map((w: any) => ({ ...w, clock_on: w?.clock_on || w?.start_time, groups: arr(w?.groups), job_roles: arr(w?.job_roles) }))
  return { today: r?.today, workers, on_timesheet: Number(r?.on_timesheet) || 0, earlier_open: Number(r?.earlier_open) || 0 }
}

// The roster's filters. Each offers only what someone clocked in now has, plus "None" when someone has no group or role;
// a person with several groups or roles matches any of them.
export const NONE = '__none__'
export interface RosterFilter {
  group: string
  team: string
  role: string
}
export const NO_FILTER: RosterFilter = { group: '', team: '', role: '' }

export function rosterOptions(workers: RosterWorker[]): { groups: string[]; teams: string[]; roles: string[]; noGroup: boolean; noRole: boolean } {
  const sorted = (xs: string[]) => [...new Set(xs.filter(Boolean))].sort((a, b) => a.localeCompare(b))
  return {
    groups: sorted(workers.flatMap((w) => w.groups)),
    teams: sorted(workers.map((w) => w.team_name)),
    roles: sorted(workers.flatMap((w) => w.job_roles)),
    noGroup: workers.some((w) => !w.groups.length),
    noRole: workers.some((w) => !w.job_roles.length),
  }
}

export function filterRoster(workers: RosterWorker[], f: RosterFilter): RosterWorker[] {
  const has = (xs: string[], v: string) => !v || (v === NONE ? !xs.length : xs.includes(v))
  return workers.filter((w) => has(w.groups, f.group) && (!f.team || w.team_name === f.team) && has(w.job_roles, f.role))
}

// ─── cards ───────────────────────────────────────────────────────────────────

export const WIDGETS = ['kpis', 'weekly', 'receipters', 'leaderboard', 'roster', 'weather'] as const
export type WidgetId = (typeof WIDGETS)[number]
// Cards that start off: a person turns them on, and the choice is saved as
// turned on (dashboard.widgets_on) rather than as turned off.
export const WIDGETS_OPT_IN: readonly WidgetId[] = ['receipters']

// A board's total row, over every row (the collapsed ones too): hours, items
// and KGs summed, KG/Hr as the total KGs over the total hours, and Hrly as the
// average weighted by hours (a shift with nothing picked yet left out of it).
export function leaderTotal(rows: LeaderRow[]): { hours: number; hourly: number | null; items: number; kg: number; kg_hour: number } {
  const r1 = (n: number) => Math.round(n * 10) / 10
  const hours = rows.reduce((t, r) => t + r.hours, 0)
  const kg = rows.reduce((t, r) => t + r.kg, 0)
  const paid = rows.filter((r) => typeof r.hourly === 'number' && !r.no_picks_yet)
  const paidHours = paid.reduce((t, r) => t + r.hours, 0)
  return {
    hours: Math.round(hours * 100) / 100,
    hourly: paidHours > 0 ? Math.round((paid.reduce((t, r) => t + (r.hourly as number) * r.hours, 0) / paidHours) * 100) / 100 : null,
    items: rows.reduce((t, r) => t + r.items, 0),
    kg: r1(kg),
    kg_hour: hours > 0 ? r1(kg / hours) : 0,
  }
}

// With the receipters board on, the leaderboard is pickers only: whoever is on
// the board comes out, and the rest are ranked again in the same order.
export function pickersOnly(rows: LeaderRow[], receipters: LeaderRow[]): LeaderRow[] {
  const out = new Set(receipters.map((r) => r.human_id))
  return rows.filter((r) => !out.has(r.human_id)).map((r, i) => ({ ...r, rank: i + 1 }))
}

// The Customize drawer's groups, in V4's words.
export const WIDGET_GROUPS: { group: string; items: { id: WidgetId; name: string; desc: string }[] }[] = [
  { group: 'Summary', items: [{ id: 'kpis', name: 'Key figures', desc: 'Total KGs, average $/KG, and KG per hour for the period' }] },
  { group: 'Charts', items: [{ id: 'weekly', name: 'KGs by week', desc: 'This season against last, week 39 through week 12' }] },
  {
    group: 'Tables',
    items: [
      { id: 'receipters', name: 'Receipters', desc: 'What each receipter received, above a leaderboard of pickers only. For everyone in the workspace' },
      { id: 'leaderboard', name: 'Worker leaderboard', desc: 'Everyone ranked by hourly earn, with the underperformance flag' },
      { id: 'roster', name: 'Clocked in now', desc: 'Live roster, with clock all off' },
    ],
  },
  { group: 'Conditions', items: [{ id: 'weather', name: 'Weather', desc: 'Current conditions and a four-day forecast for your site' }] },
]

// Two cards share a row; with one of them hidden the other takes the width
// (V4 cols()).
export function rowCols(a: boolean, b: boolean) {
  return a && b ? 2 : a || b ? 1 : 0
}

// ─── the season and the presets ──────────────────────────────────────────────

// The server's DASH-5 rule, repeated so "This season" starts where the
// weekly chart does: ISO week 39 of the season's year.
export function isoWeekOf(date: string) {
  const [y, m, d] = date.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d))
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7))
  const year = t.getUTCFullYear()
  return { year, week: Math.ceil(((t.getTime() - Date.UTC(year, 0, 1)) / 86400000 + 1) / 7) }
}

export function mondayOf(year: number, week: number) {
  const jan4 = new Date(Date.UTC(year, 0, 4))
  jan4.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7) + (week - 1) * 7)
  return jan4.toISOString().slice(0, 10)
}

export function seasonOf(date: string) {
  const { year, week } = isoWeekOf(date)
  return week >= 39 ? year : year - 1
}

export const seasonStartOf = (today: string) => mondayOf(seasonOf(today), 39)

export function mondayOfDate(iso: string) {
  return addDaysLocal(iso, -((parseIsoLocal(iso).getDay() + 6) % 7))
}

// V4 dashCal presets; Today / Week / Season are also the toolbar's tabs.
export function dashPresets(today: string): Preset[] {
  return [
    ['Today', today, today],
    ['This week', mondayOfDate(today), today],
    ['Last 7 days', addDaysLocal(today, -6), today],
    ['This season', seasonStartOf(today), today],
  ]
}

export function scopeOf(from: string, to: string, today: string): 'today' | 'week' | 'season' | null {
  if (to !== today) return null
  if (from === today) return 'today'
  if (from === mondayOfDate(today)) return 'week'
  if (from === seasonStartOf(today)) return 'season'
  return null
}

// The dots (V4 dashCal): a busy picking day, some picking, or timesheets
// with no picking recorded.
export function dayLevel(cycles: number, shifts: number): Level {
  if (cycles >= 6) return 3
  if (cycles > 0) return 2
  return shifts > 0 ? 1 : 0
}

export const DASH_LEGEND: [Level, string][] = [
  [3, 'Busy day'],
  [2, 'Some picking'],
  [1, 'Timesheets only'],
]

// ─── formatting ──────────────────────────────────────────────────────────────

export const num = (n: number, dp = 0) => n.toLocaleString('en-NZ', { minimumFractionDigits: dp, maximumFractionDigits: dp })
export const money = (n: number) => '$' + num(n, 2)

export function kpiValue(k: Kpi) {
  if (k.value === null) return '—'
  if (k.key === 'kg_items') return num(k.value, 1)
  if (k.key === 'kg_hour') return num(k.value, 1)
  return money(k.value)
}

// The line under a KPI: which way it moved against the window before, and
// how to read that (DASH-3: a cost per kg carries no judgement).
export function kpiDelta(k: Kpi): { text: string; tone: 'good' | 'bad' | 'neutral' } {
  if (k.change_pct === null) return { text: k.previous === null ? `No figure ${k.compare_label.replace(/^vs /, 'for the ')}` : `Nothing to compare ${k.compare_label.replace(/^vs /, 'with the ')}`, tone: 'neutral' }
  if (k.change_pct === 0) return { text: `No change ${k.compare_label}`, tone: 'neutral' }
  const up = k.change_pct > 0
  const tone = k.rise === 'neutral' ? 'neutral' : up ? 'good' : 'bad'
  // Said in words, never a bare arrow glyph (§7).
  return { text: `${up ? 'Up' : 'Down'} ${num(Math.abs(k.change_pct), 1)}% ${k.compare_label}`, tone }
}

const MONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export function dmy(iso: string) {
  const d = parseIsoLocal(iso)
  return `${d.getDate()} ${MONS[d.getMonth()]} ${d.getFullYear()}`
}
export function dm(iso: string) {
  const d = parseIsoLocal(iso)
  return `${d.getDate()} ${MONS[d.getMonth()]}`
}

export function weekTitle(start: string, week: number) {
  return `Week commencing ${dmy(start)}: ends ${dm(addDaysLocal(start, 6))} (week ${week})`
}

// The tenant's wall clock (§9): a clock-off "now" is the business's time,
// not the viewer's.
export function nowHm(d = new Date()) {
  return nowHmBusiness(d)
}

// ─── weather ─────────────────────────────────────────────────────────────────

export type WxIcon = 'sun' | 'part' | 'cloud' | 'fog' | 'rain' | 'snow' | 'storm'

// WMO weather codes to the icon set (h 585): 0 clear, 1-2 partly cloudy, 3
// overcast, 45-48 fog, 51-67 rain, 71-77 snow, 80-82 showers, 95+ storm.
// V4 drew snow as a bare cloud; it has its own glyph. 85-86 are WMO's snow
// showers, which V4 read as a thunderstorm.
export function wxMeta(code: number): { label: string; icon: WxIcon } {
  if (code === 0) return { label: 'Clear', icon: 'sun' }
  if (code <= 2) return { label: 'Partly cloudy', icon: 'part' }
  if (code === 3) return { label: 'Overcast', icon: 'cloud' }
  if (code <= 48) return { label: 'Fog', icon: 'fog' }
  if (code <= 67) return { label: 'Rain', icon: 'rain' }
  if (code <= 77) return { label: 'Snow', icon: 'snow' }
  if (code <= 82) return { label: 'Showers', icon: 'rain' }
  if (code === 85 || code === 86) return { label: 'Snow showers', icon: 'snow' }
  return { label: 'Thunderstorms', icon: 'storm' }
}

export const WX_PATHS: Record<WxIcon, string[]> = {
  sun: ['M12 4V2M12 22v-2M4 12H2M22 12h-2M5.6 5.6 4.2 4.2M19.8 19.8l-1.4-1.4M18.4 5.6l1.4-1.4M4.2 19.8l1.4-1.4'],
  part: ['M12 5V3M5.6 6.6 4.2 5.2M19.8 5.2l-1.4 1.4M4 12H2', 'M16.5 19a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.3 2A3.5 3.5 0 0 0 6 19h10.5Z'],
  cloud: ['M17.5 19a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.3 2A3.5 3.5 0 0 0 7 19h10.5Z'],
  rain: ['M17.5 15a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.3 2A3.5 3.5 0 0 0 7 15h10.5Z', 'M9 19v2M13 18v3M17 19v2'],
  // Flakes as dots, so snow never reads as rain's streaks.
  snow: ['M17.5 15a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.3 2A3.5 3.5 0 0 0 7 15h10.5Z', 'M8 19h.01M12 19h.01M16 19h.01M10 22h.01M14 22h.01'],
  storm: ['M17.5 14a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.3 2A3.5 3.5 0 0 0 7 14', 'M13 12l-3 5h4l-2 5'],
  fog: ['M16.5 13a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.3 2A3.5 3.5 0 0 0 6 13', 'M4 17h16M6 21h12'],
}

export interface WxLocation {
  name: string
  lat: number
  lon: number
}

export interface Forecast {
  temp: number
  code: number
  wind: number
  hi: number
  lo: number
  days: { date: string; code: number; hi: number; lo: number }[]
}

export function forecastUrl(l: WxLocation) {
  return (
    `https://api.open-meteo.com/v1/forecast?latitude=${l.lat}&longitude=${l.lon}` +
    '&current=temperature_2m,weather_code,wind_speed_10m&daily=weather_code,temperature_2m_max,temperature_2m_min&timezone=auto&forecast_days=5'
  )
}

export function geocodeUrl(q: string) {
  return `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=6&language=en&format=json`
}

export function parseForecast(j: any): Forecast | null {
  const c = j?.current
  const d = j?.daily
  if (!c || !d || !Array.isArray(d.time)) return null
  const n = (v: any) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
  const temp = n(c.temperature_2m)
  const code = n(c.weather_code)
  const hi = n(d.temperature_2m_max?.[0])
  const lo = n(d.temperature_2m_min?.[0])
  if (temp === null || code === null || hi === null || lo === null) return null
  const days: Forecast['days'] = []
  for (let i = 1; i <= 4 && i < d.time.length; i++) {
    const dc = n(d.weather_code?.[i])
    const dh = n(d.temperature_2m_max?.[i])
    const dl = n(d.temperature_2m_min?.[i])
    if (dc === null || dh === null || dl === null) continue
    days.push({ date: String(d.time[i]), code: dc, hi: Math.round(dh), lo: Math.round(dl) })
  }
  return { temp: Math.round(temp), code, wind: Math.round(n(c.wind_speed_10m) ?? 0), hi: Math.round(hi), lo: Math.round(lo), days }
}

export function parsePlaces(j: any): (WxLocation & { meta: string })[] {
  const r2 = (v: number) => Math.round(v * 100) / 100
  return (Array.isArray(j?.results) ? j.results : [])
    .filter((p: any) => typeof p?.latitude === 'number' && typeof p?.longitude === 'number' && p?.name)
    .map((p: any) => ({ name: String(p.name), meta: [p.admin1, p.country].filter(Boolean).join(', '), lat: r2(p.latitude), lon: r2(p.longitude) }))
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
export const dowOf = (iso: string) => DOW[parseIsoLocal(iso).getDay()]
